import { Platform } from '@angular/cdk/platform';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { delay, filter, map, take, takeUntil } from 'rxjs/operators';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { StorageKeyEnum } from '@app/core/enums/storage-key.enum';
import { ToolsHelper } from '@app/core/helpers/tools.helper';
import { ProfileModel, UserModel } from '@app/core/models';
import { SnackbarService } from '@app/core/services';
import {
  AnalyticsEvents,
  AnalyticsService,
} from '@app/core/services/analytics.service';
import { APP_CONFIG, IAppConfig } from '@app/core/services/app.config';
import { ErrorMessageService } from '@app/core/services/error-message.service';
import { EventsService } from '@app/core/services/events.service';
import { StorageService } from '@app/core/services/storage.service';
import { UserService } from '@app/core/services/user.service';
import { BtrEventApiService } from '@app/shared/services/api';

export enum AuthSteps {
  PhoneInputStep,
  NameInputStep,
  ConfirmStep,
}

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss', './auth-modals.component.scss'],
})
export class AuthComponent implements OnInit, OnDestroy {
  @Input() wlMode = false;
  @Input() standalone = false;
  @Input() boxBtnMode = false;
  @Input() opened = false;
  @Input() whiteMode = false;
  @Input() checkInPage = false;
  @Input() renderTrigger = true;
  @Input() renderModal = true;

  @Output()
  toggleMenu: EventEmitter<boolean | void> = new EventEmitter();

  @ViewChild('phoneInput') public phoneInput: ElementRef;
  @ViewChild('phoneSignUpInput') public phoneSignUpInput: ElementRef;
  @ViewChild('nameInput') public nameInput: ElementRef;
  @ViewChild('lastNameInput') public lastNameInput: ElementRef;
  @ViewChild('code1') public code1: ElementRef;
  @ViewChild('code2') public code2: ElementRef;
  @ViewChild('code3') public code3: ElementRef;
  @ViewChild('code4') public code4: ElementRef;

  phoneControl: FormControl;
  phoneSignUpControl: FormControl;
  nameControl: FormControl;
  lastNameControl: FormControl;
  firstStepInValid = false;
  firstStepSignUpInValid = false;
  signUpStep: AuthSteps = AuthSteps.PhoneInputStep;
  loginStep: AuthSteps = AuthSteps.PhoneInputStep;
  steps = AuthSteps;
  codeGroup: FormGroup;
  user: UserModel;
  profile: ProfileModel;
  nextDisabled = true;
  signUpDisabled = true;
  loginDisabled = true;
  showLoginDialog = false;
  showSignUpDialog = false;
  mobileInfoShow = false;
  resendActive = true;
  loading = false;
  errorMessage: string;
  public counts = {
    yTickets: 0,
    yPassports: 0,
  };

  private destroy$: Subject<void> = new Subject();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    @Inject(APP_CONFIG) public readonly appConfig: IAppConfig,
    @Inject(DOCUMENT) private document: Document,
    private userService: UserService,
    private storageService: StorageService,
    private eventsService: EventsService,
    // private authService: SocialAuthService,
    private analyticsService: AnalyticsService,
    private snackbarService: SnackbarService,
    private errorMessageService: ErrorMessageService,
    private readonly btrEventApiService: BtrEventApiService,
  ) {
    this.eventsService.showSignUpDialog$
      .pipe(takeUntil(this.destroy$))
      .subscribe(showSignUpDialog => {
        this.showSignUpDialog = showSignUpDialog;
        this.nextDisabled = showSignUpDialog;
      });
  }

  ngOnInit(): void {
    this.user = this.storageService.USER;
    this.profile = this.storageService.PROFILE;
    this.eventsService.ticketPurchaseComplete$
      .pipe(
        filter(val => val),
        takeUntil(this.destroy$),
        delay(300),
      )
      .subscribe(() => {
        this.user = this.storageService.USER;
        this.profile = this.storageService.PROFILE;
        this.eventsService.ticketPurchaseCompleteSet(false);
      });
    if (this.user) {
      this.userService
        .getUser()
        .pipe(takeUntil(this.destroy$))
        .subscribe(resp => {
          this.storageService.USER = resp.user;
          this.user = resp.user;
          this.storageService.PROFILE = resp.profile;
          this.profile = resp.profile;
          this.eventsService.setUserDoneSet(true);
          this.setPassports();
        });
    }
    this.eventsService.forceLogout$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        if (error) {
          this.user = null;
          this.profile = null;
          this.resetCodeGroup();
          this.logout();
        }
      });
    this.phoneControl = new FormControl();
    this.phoneControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.loginDisabled = true;
        this.loginDisabled = (value as string).length < 11;
      });
    this.phoneControl.patchValue(1);
    this.phoneSignUpControl = new FormControl();
    this.phoneSignUpControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.nextDisabled = (value as string).length < 11;
      });
    this.nameControl = new FormControl('', [Validators.required]);
    this.nameControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signUpDisabledCheck();
      });
    this.lastNameControl = new FormControl('', [Validators.required]);
    this.lastNameControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signUpDisabledCheck();
      });
    this.codeGroup = new FormGroup({
      code1: new FormControl(),
      code2: new FormControl(),
      code3: new FormControl(),
      code4: new FormControl(),
    });
    this.eventsService.signInComplete$
      .pipe(delay(100), takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.user = this.storageService.USER;
        }
      });
    this.eventsService.openLogin$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.showSignUpDialog = true;
          this.eventsService.setShowSignUpDialog(this.showSignUpDialog);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  signUpNext(isConfirm: boolean = false): void {
    if (this.nextDisabled) {
      return;
    }

    this.errorMessage = null;
    this.loading = true;
    if (isConfirm) {
      if (this.nameControl.invalid || this.lastNameControl.invalid) {
        this.errorMessage = '';
        if (this.nameControl.invalid) {
          this.errorMessage = this.errorMessage + 'Please enter First name. ';
        }
        if (this.lastNameControl.invalid) {
          this.errorMessage = this.errorMessage + 'Please enter Last name.';
        }
        return;
      }
      this.signUpDisabled = true;
      this.userService
        .signUp(
          this.phoneSignUpControl.value,
          this.nameControl.value,
          this.lastNameControl.value,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          response => {
            this.loading = false;
            const token = response.headers.get('X-Authorization-Update');
            this.storageService.TOKEN = token;
            if (response.body.success) {
              this.setUser(response);
              // this.eventsService.signInCompleteSet(true);
            }
          },
          error => {
            this.loading = false;
            this.errorMessage = this.errorMessageService.getError(error);
            this.snackbarService.showError(
              this.errorMessageService.getError(error),
            );
          },
        );
    } else {
      this.analyticsService.emitEvent(
        AnalyticsEvents.navigationSignUpNextButton,
        {},
      );
      this.userService
        .signIn(this.phoneSignUpControl.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.loading = false;
            this.signUpStep = AuthSteps.ConfirmStep;
          },
          error => {
            this.loading = false;
            this.errorMessage = this.errorMessageService.getError(error);
            this.snackbarService.showError(
              this.errorMessageService.getError(error),
            );
          },
        );
    }
  }

  clickAnalyticSend(what: string): void {
    this.modalViewClose();
    switch (what) {
      case 'pp':
        this.analyticsService.emitEvent(AnalyticsEvents.homePP, {});
        break;
      case 'tos':
        this.analyticsService.emitEvent(AnalyticsEvents.homeToS, {});
        break;
    }
  }

  modalViewClose(): void {
    this.showLoginDialog = false;
    this.showSignUpDialog = false;
    this.eventsService.setShowSignUpDialog(this.showSignUpDialog);
    this.phoneControl.patchValue(1);
    this.phoneSignUpControl.patchValue(1);
    this.nameControl.patchValue('');
    this.lastNameControl.patchValue('');
    this.loginStep = AuthSteps.PhoneInputStep;
    this.signUpStep = AuthSteps.PhoneInputStep;
    this.loading = false;
    this.errorMessage = null;
  }

  verify(event, field: number, isSignUp = false): void {
    if (event.key === 'Backspace') {
      switch (field) {
        case 1:
          break;
        case 2:
          if (this.codeGroup?.controls?.code2?.value?.length === 0) {
            this.code1.nativeElement.focus();
          }
          break;
        case 3:
          if (this.codeGroup?.controls?.code3?.value?.length === 0) {
            this.code2.nativeElement.focus();
          }
          break;
        case 4:
          if (this.codeGroup?.controls?.code4?.value?.length === 0) {
            this.code3.nativeElement.focus();
          }
          break;
      }
    } else {
      switch (field) {
        case 1:
          this.code2.nativeElement.focus();
          break;
        case 2:
          this.code3.nativeElement.focus();
          break;
        case 3:
          this.code4.nativeElement.focus();
          break;
      }
    }

    if (this.codeGroup?.controls?.code4?.value) {
      const code =
        this.codeGroup?.controls?.code1.value +
        this.codeGroup?.controls?.code2.value +
        this.codeGroup?.controls?.code3.value +
        this.codeGroup?.controls?.code4.value;
      let phone = '';
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.errorMessage = null;
      phone = isSignUp
        ? this.phoneSignUpControl.value
        : this.phoneControl.value;
      this.eventsService.setLoginInProgress(true);
      this.userService
        .verify(phone, code)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          response => {
            const token = response.headers.get('X-Authorization-Update');
            this.storageService.TOKEN = token;
            if (response.body.success && response.body.user) {
              this.setUser(response);

              this.loading = false;
              this.showSignUpDialog = false;
              this.eventsService.setShowSignUpDialog(this.showSignUpDialog);
            } else {
              this.storageService.removeKey(StorageKeyEnum.TOKEN);
              this.signUpStep = AuthSteps.NameInputStep;
              this.loading = false;
              this.eventsService.setLoginInProgress(false);
            }
          },
          error => {
            this.loading = false;
            this.errorMessage = this.errorMessageService.getError(error);
            this.snackbarService.showError(
              this.errorMessageService.getError(error),
            );
            this.eventsService.setLoginInProgress(false);
            this.eventsService.errorOnLogin$.next(true);
          },
        );
    }
  }

  resend(from: string): void {
    if (from === 'login') {
      this.analyticsService.emitEvent(
        AnalyticsEvents.navigationLoginResendCode,
        {},
      );
      this.userService
        .signIn(this.phoneControl.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          if (response.success) {
            this.resendActive = false;
          }
        });
    } else {
      this.analyticsService.emitEvent(
        AnalyticsEvents.navigationSignUpResendCode,
        {},
      );
      this.userService
        .signIn(this.phoneSignUpControl.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          if (res.success) {
            this.resendActive = false;
          }
        });
    }
  }

  backStep(isLogin: boolean = false): void {
    this.errorMessage = null;
    this.resetCodeGroup();
    if (isLogin) {
      this.loginStep = AuthSteps.PhoneInputStep;
    } else {
      this.signUpStep = AuthSteps.PhoneInputStep;
    }
  }

  loginFb(from: string): void {
    if (from === 'login') {
      this.analyticsService.emitEvent(
        AnalyticsEvents.navigationLoginFacebook,
        {},
      );
    } else {
      this.analyticsService.emitEvent(
        AnalyticsEvents.navigationSignUpFacebook,
        {},
      );
    }

    //   this.authService.authState
    //     .pipe(
    //       switchMap(user => {
    //         if (user) {
    //           this.storageService.TOKEN = `Facebook ${user.authToken}`;
    //
    //           return this.userService
    //             .getUserFacebook()
    //             .pipe(takeUntil(this.destroy$));
    //         }
    //
    //         throw new Error('Error of facebook auth');
    //       }),
    //       takeUntil(this.destroy$),
    //     )
    //     .subscribe(
    //       response => {
    //         this.setUser(response);
    //         this.eventsService.signInCompleteSet(true);
    //       },
    //       err => {
    //         console.error(err);
    //       },
    //     );
    //   this.authService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  logout(): void {
    this.storageService.removeKey(StorageKeyEnum.USER);
    this.storageService.removeKey(StorageKeyEnum.TOKEN);
    this.storageService.removeKey(StorageKeyEnum.PLANS);
    this.storageService.removeKey(StorageKeyEnum.PROFILE);
    this.toggleMenu.next(false);
    if (isPlatformBrowser(this.platformId)) {
      this.document.location.reload();
    } else {
      this.user = undefined;
      this.profile = undefined;
    }
  }

  openProfilePopup(event, isText = false): void {
    if (isText) {
      const element: HTMLElement = this.document.querySelector(
        '#userPhoto',
      ) as HTMLElement;
      const eventSimulation = new MouseEvent('mouseover', {
        view: window,
        bubbles: true,
        cancelable: true,
      });
      element.dispatchEvent(eventSimulation);
    }
    event.stopPropagation();
    this.analyticsService.emitEvent(AnalyticsEvents.navigationProfile, {});

    if (window.screen.width < 768) {
      this.mobileInfoShow = !this.mobileInfoShow;
    } else {
      this.toggleMenu.next();
    }
  }

  onClickedOutside(): void {
    this.toggleMenu.next(false);
    const element: HTMLElement = this.document.querySelector(
      '#userPhoto',
    ) as HTMLElement;
    const eventSimulation = new MouseEvent('mouseover', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    element.dispatchEvent(eventSimulation);
  }

  getImage(isSimple: boolean = true): any {
    if (isSimple) {
      return {
        'background-image': ToolsHelper.initBackground(this.user.image),
      };
    }

    return {
      'background-image': ToolsHelper.initBackground(
        this.user.image,
        `/${this.appConfig.settings.images_path}/profile-icon-green.svg`,
      ),
    };
  }

  showSignUpDialogHandler(): void {
    this.showSignUpDialog = !this.showSignUpDialog;
    this.eventsService.setShowSignUpDialog(this.showSignUpDialog);
    this.nextDisabled = true;
  }

  private signUpDisabledCheck(): void {
    this.signUpDisabled =
      (this.nameControl.value as string).length === 0 ||
      (this.lastNameControl.value as string).length === 0;
  }

  private setUser(response): void {
    let user;
    if (response.body && response.body.user) {
      user = response.body.user;
    }

    if (response.user) {
      user = response.user;
    }

    if (user) {
      this.userService
        .getUser()
        .pipe(takeUntil(this.destroy$))
        .subscribe(resp => {
          this.user = resp.user;
          this.storageService.USER = resp.user;
          this.storageService.PROFILE = resp.profile;
          this.eventsService.setUserDoneSet(true);
          this.modalViewClose();
          this.eventsService.signInCompleteSet(true);
        });
    } else {
      this.eventsService.errorHappensSource('Auth error');
    }
  }

  private setPassports(): void {
    let ticketCount = 0;
    let passportCount = 0;
    if (this.storageService?.CONFIG?.cluster && this.checkInPage) {
      this.btrEventApiService
        .getEvents({
          output_fields: ['beacons', 'tickets'].join(','),
          filters: `actual,expired,paid,free`,
          cluster: this.storageService.CONFIG.cluster,
        })
        .pipe(
          map(events => {
            {
              const currentEvent = this.storageService.EVENT;
              if (!!currentEvent) {
                const freshDataEvent = events.find(
                  item => item.id === currentEvent.id,
                );
                if (freshDataEvent) {
                  this.storageService.EVENT = freshDataEvent;
                }
              }
              this.counts.yTickets = 0;
              this.counts.yPassports = 0;
              const tickets = this.profile.tickets;
              const eventsIds = [
                ...new Set(this.profile.tickets.map(ticket => ticket.event)),
              ];
              eventsIds.map(eventId => {
                const neededEvent = events.find(event => event.id === eventId);
                if (!neededEvent) {
                  return;
                }
                const neededTickets = tickets.filter(
                  ticket => ticket.event === eventId && !ticket.bundle?.length,
                );
                for (const ticket of neededTickets) {
                  if (ticket.isPassport) {
                    passportCount += ticket.statuses.length;
                  } else {
                    ticketCount += ticket.statuses.length;
                  }
                }
                this.counts.yPassports = passportCount;
                this.counts.yTickets = ticketCount;
              });
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  private resetCodeGroup(): void {
    this.codeGroup.setValue({
      code1: null,
      code2: null,
      code3: null,
      code4: null,
    });
  }
}
